/* UsersTab — global Super-Admin user management.
 *
 * Lifted out of Academy → Records so Super Admin (and division admins,
 * scoped) can reset passwords, revoke access, and restore access for any
 * user across Systems, Services and Academy in one place.
 *
 * Auth: cookie-based JWT. Reuses the admin password stored in localStorage
 * (same path as AcademyAdminPanel) to bootstrap the session on first load.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Users, Search, RefreshCw, KeyRound, ShieldOff, ShieldCheck, X, Copy, Check,
  AlertTriangle, Filter, Crown, GraduationCap, Wrench, ShoppingBag,
} from 'lucide-react';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const api = axios.create({ baseURL: API, withCredentials: true });

const ROLE_LABEL = {
  admin: 'Admin', student: 'Student', parent: 'Parent', instructor: 'Instructor',
};
const DIVISION_META = {
  systems:  { label: 'Systems',  Icon: Wrench,         tone: 'bg-orange-100 text-orange-800 border-orange-200' },
  services: { label: 'Services', Icon: ShoppingBag,    tone: 'bg-amber-100  text-amber-800  border-amber-200'  },
  academy:  { label: 'Academy',  Icon: GraduationCap,  tone: 'bg-purple-100 text-purple-800 border-purple-200' },
};

function Pill({ children, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.08em] ${className}`}>
      {children}
    </span>
  );
}

function CopyChip({ value, label }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={`copy-${label}`}
      className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-mono font-semibold text-amber-900 transition hover:bg-amber-100"
    >
      <code className="truncate max-w-[260px]">{value}</code>
      {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

function ResetPasswordModal({ user, tempPassword, onClose }) {
  if (!user) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="reset-pw-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Password reset</h3>
            <p className="text-sm text-gray-500">A fresh temp password was emailed to <strong>{user.email}</strong>. Active sessions for this account were also signed out.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-amber-700 mb-2">One-time temporary password</div>
          <CopyChip value={tempPassword} label="temp-password" />
          <p className="mt-3 text-[12px] text-amber-900">
            Share this securely with the user. They should change it after first sign-in.
            We won't show this value again.
          </p>
        </div>
        <button
          onClick={onClose}
          data-testid="reset-pw-modal-close"
          className="mt-5 inline-flex w-full items-center justify-center rounded-full bg-gray-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-gray-700"
        >
          Done
        </button>
      </div>
    </div>
  );
}

function RevokeConfirmModal({ user, onClose, onConfirm, busy }) {
  if (!user) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="revoke-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 rounded-full bg-red-100 p-2.5">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Revoke access for {user.email}?</h3>
            <p className="text-sm text-gray-500 mt-1">
              This signs every active session out, blocks all future logins (including 2FA challenges in flight), and disables the account until a Super Admin restores it.
            </p>
          </div>
        </div>
        <ul className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-1.5 text-[12.5px] text-red-900">
          <li>• Every JWT issued to this user is invalidated immediately.</li>
          <li>• In-flight 2FA challenges are wiped.</li>
          <li>• Login attempts return <code className="bg-white/60 px-1 rounded">403 Account disabled</code>.</li>
          <li>• Audit log records the actor + division + role.</li>
        </ul>
        <div className="mt-5 flex items-center justify-end gap-3">
          <button onClick={onClose} className="rounded-full px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="revoke-confirm"
            className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />}
            {busy ? 'Revoking…' : 'Revoke access'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UsersTab({ adminPassword }) {
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [users, setUsers] = useState([]);
  const [scope, setScope] = useState('super');
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState(() => {
    // Honour ?q=email from the Audit Log deep-link so admins land on the
    // exact user they were inspecting in the audit row.
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('q') || '';
  });
  const [roleFilter, setRoleFilter] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '', 'active', 'disabled'
  const [resetTarget, setResetTarget] = useState(null);
  const [resetTempPassword, setResetTempPassword] = useState('');
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [actionBusy, setActionBusy] = useState('');
  const [toast, setToast] = useState('');

  // Bootstrap JWT session.
  // Try the existing cookie first (set by SuperAdminLogin or a division
  // login). Fall back to logging in as the seeded super-admin only if the
  // legacy admin password was provided AND cookie auth failed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.get('/api/auth/me');
        if (!cancelled) setAuthed(true);
        return;
      } catch { /* fall through */ }
      if (!adminPassword) {
        if (!cancelled) setAuthError('Sign in via /zhelta-internal/console or your division portal first.');
        return;
      }
      try {
        await api.post('/api/auth/login', { email: 'admin@zhelta.com', password: adminPassword });
        if (!cancelled) setAuthed(true);
      } catch (err) {
        if (!cancelled) setAuthError(err.response?.data?.detail || err.message || 'Login failed');
      }
    })();
    return () => { cancelled = true; };
  }, [adminPassword]);

  const reload = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (roleFilter) params.role = roleFilter;
      if (divisionFilter) params.division = divisionFilter;
      const { data } = await api.get('/api/academy/admin/users', { params });
      setUsers(data.rows || []);
      setScope(data.actor_scope || 'super');
    } catch (err) {
      setToast(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [authed, q, roleFilter, divisionFilter]);

  useEffect(() => { reload(); }, [reload]);

  // Auto-clear toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const filteredUsers = useMemo(() => {
    if (!statusFilter) return users;
    return users.filter(u =>
      statusFilter === 'disabled' ? u.disabled : !u.disabled,
    );
  }, [users, statusFilter]);

  const handleResetPassword = async (user) => {
    setActionBusy(`reset-${user.id}`);
    try {
      const { data } = await api.post(`/api/academy/admin/users/${user.id}/reset-password`);
      setResetTarget(user);
      setResetTempPassword(data.temp_password);
      reload();
    } catch (err) {
      setToast(err.response?.data?.detail || 'Reset failed');
    } finally {
      setActionBusy('');
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setActionBusy(`revoke-${revokeTarget.id}`);
    try {
      await api.post(`/api/academy/admin/users/${revokeTarget.id}/revoke-access`);
      setToast(`Access revoked for ${revokeTarget.email}`);
      setRevokeTarget(null);
      reload();
    } catch (err) {
      setToast(err.response?.data?.detail || 'Revoke failed');
    } finally {
      setActionBusy('');
    }
  };

  const handleRestore = async (user) => {
    setActionBusy(`restore-${user.id}`);
    try {
      await api.post(`/api/academy/admin/users/${user.id}/restore-access`);
      setToast(`Access restored for ${user.email}`);
      reload();
    } catch (err) {
      setToast(err.response?.data?.detail || 'Restore failed');
    } finally {
      setActionBusy('');
    }
  };

  if (authError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-900">
        <div className="font-bold mb-1">Sign-in required</div>
        <div className="text-sm">{authError}</div>
      </div>
    );
  }
  if (!authed) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 text-gray-500 text-sm">
        Authenticating Super-Admin session…
      </div>
    );
  }

  const isSuper = scope === 'super';

  return (
    <div data-testid="users-tab" className="space-y-5">
      {/* Header */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-5 w-5 text-[#c9a227]" />
              <h2 className="text-lg font-bold text-gray-900">User Management</h2>
              {isSuper ? (
                <Pill className="bg-yellow-100 text-yellow-900 border-yellow-300">
                  <Crown className="h-3 w-3" /> Super Admin
                </Pill>
              ) : (
                <Pill className={DIVISION_META[scope]?.tone || 'bg-gray-100 text-gray-700 border-gray-300'}>
                  {DIVISION_META[scope]?.label || scope} Division
                </Pill>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {isSuper
                ? 'You can reset passwords, revoke and restore access for any user across Systems, Services and Academy.'
                : `Showing only ${DIVISION_META[scope]?.label || scope} users you're authorised to manage. Other admin accounts are hidden by design.`}
            </p>
          </div>
          <button
            onClick={reload}
            disabled={loading}
            data-testid="users-refresh"
            className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email or name…"
              data-testid="users-search"
              className="w-full rounded-lg border border-gray-200 pl-10 pr-3 py-2 text-sm focus:border-[#c9a227] focus:outline-none"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            data-testid="users-role-filter"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#c9a227] focus:outline-none"
          >
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="student">Student</option>
            <option value="parent">Parent</option>
            <option value="instructor">Instructor</option>
          </select>
          <div className="flex gap-2">
            {isSuper && (
              <select
                value={divisionFilter}
                onChange={(e) => setDivisionFilter(e.target.value)}
                data-testid="users-division-filter"
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#c9a227] focus:outline-none"
              >
                <option value="">All divisions</option>
                <option value="systems">Systems</option>
                <option value="services">Services</option>
                <option value="academy">Academy</option>
              </select>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="users-status-filter"
              className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#c9a227] focus:outline-none"
            >
              <option value="">Any status</option>
              <option value="active">Active only</option>
              <option value="disabled">Disabled only</option>
            </select>
          </div>
        </div>
        <div className="mt-2 text-[11.5px] text-gray-500 flex items-center gap-1.5">
          <Filter className="h-3 w-3" />
          {filteredUsers.length} of {users.length} users {q && <span>matching "<strong>{q}</strong>"</span>}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div data-testid="users-toast" className="rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      {/* Users table */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[11px] font-bold uppercase tracking-[0.1em] text-gray-600">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Division</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400 text-sm">
                    {loading ? 'Loading…' : 'No users match the current filters.'}
                  </td>
                </tr>
              )}
              {filteredUsers.map((u) => {
                const div = DIVISION_META[u.division] || DIVISION_META.academy;
                const DivIcon = div.Icon;
                const created = u.created_at ? new Date(u.created_at).toLocaleDateString() : '—';
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email}`} className={u.disabled ? 'bg-red-50/40' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900 flex items-center gap-1.5">
                        {u.email}
                        {u.is_super_admin && <Crown className="h-3.5 w-3.5 text-yellow-600" title="Super Admin (protected)" />}
                        {u.totp_enabled && <Pill className="bg-emerald-100 text-emerald-800 border-emerald-200">2FA</Pill>}
                      </div>
                      <div className="text-[12px] text-gray-500">{u.name || '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Pill className="bg-gray-100 text-gray-800 border-gray-200">{ROLE_LABEL[u.role] || u.role}</Pill>
                    </td>
                    <td className="px-4 py-3">
                      <Pill className={div.tone}>
                        <DivIcon className="h-3 w-3" /> {div.label}
                      </Pill>
                    </td>
                    <td className="px-4 py-3">
                      {u.disabled ? (
                        <Pill className="bg-red-100 text-red-800 border-red-300">
                          <ShieldOff className="h-3 w-3" /> Disabled
                        </Pill>
                      ) : (
                        <Pill className="bg-emerald-100 text-emerald-800 border-emerald-200">
                          <ShieldCheck className="h-3 w-3" /> Active
                        </Pill>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[12px] text-gray-500">{created}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button
                          onClick={() => handleResetPassword(u)}
                          disabled={actionBusy === `reset-${u.id}` || u.is_super_admin}
                          data-testid={`reset-pw-${u.email}`}
                          title={u.is_super_admin ? 'Super Admin password is rotated via .env' : 'Reset password'}
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1.5 text-[11.5px] font-semibold text-amber-900 hover:bg-amber-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <KeyRound className="h-3 w-3" />
                          {actionBusy === `reset-${u.id}` ? 'Resetting…' : 'Reset password'}
                        </button>
                        {u.disabled ? (
                          <button
                            onClick={() => handleRestore(u)}
                            disabled={actionBusy === `restore-${u.id}`}
                            data-testid={`restore-${u.email}`}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1.5 text-[11.5px] font-semibold text-emerald-900 hover:bg-emerald-200 disabled:opacity-50"
                          >
                            <ShieldCheck className="h-3 w-3" />
                            {actionBusy === `restore-${u.id}` ? 'Restoring…' : 'Restore access'}
                          </button>
                        ) : (
                          <button
                            onClick={() => setRevokeTarget(u)}
                            disabled={u.is_super_admin}
                            data-testid={`revoke-${u.email}`}
                            title={u.is_super_admin ? 'Super Admin cannot be revoked' : 'Revoke access'}
                            className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1.5 text-[11.5px] font-semibold text-red-900 hover:bg-red-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <ShieldOff className="h-3 w-3" />
                            Revoke access
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          tempPassword={resetTempPassword}
          onClose={() => { setResetTarget(null); setResetTempPassword(''); }}
        />
      )}
      {revokeTarget && (
        <RevokeConfirmModal
          user={revokeTarget}
          busy={actionBusy === `revoke-${revokeTarget.id}`}
          onConfirm={handleRevoke}
          onClose={() => setRevokeTarget(null)}
        />
      )}
    </div>
  );
}
